import { useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { notifier } from '../lib/notify';
import { dismissReminderCard, enableReminders } from '../state/reminderSync';
import { useStore } from '../state/store';

/** Whether to offer reminders on Today: in the app, once there's a plan, and only until answered. */
export function useReminderOffer(): boolean {
  const asked = useStore((s) => s.profile.notifications.askedAt);
  const hasPlan = useStore((s) => !!s.plan);
  return !asked && hasPlan && notifier() !== null;
}

/**
 * Asks in the app before iOS asks, so the one-time system prompt only appears for people who want
 * reminders (a declined system prompt can only be undone in iOS Settings).
 */
export function ReminderCard() {
  const time = useStore((s) => s.profile.notifications.dinnerTime);
  const showToast = useStore((s) => s.showToast);
  const [busy, setBusy] = useState(false);

  const yes = async () => {
    setBusy(true);
    const permission = await enableReminders('card');
    showToast(permission === 'granted' ? `We’ll remind you at ${formatTime(time)} on planned nights.` : 'Reminders are off. You can turn them on in iOS Settings.');
  };

  return (
    <section className="tile p-4 mb-8 fade-in" aria-labelledby="reminder-q">
      <div className="flex items-start gap-2">
        <BellRing size={22} className="text-primary shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1">
          <h2 id="reminder-q" className="text-headline">Get a reminder at dinner time?</h2>
          <p className="text-subhead text-fg-muted mt-0.5">A nudge at {formatTime(time)} on planned nights, and one the evening before you shop. Change it anytime in Profile.</p>
        </div>
        <button className="icon-btn -mr-2 -mt-2 text-fg-muted" onClick={dismissReminderCard} aria-label="Not now"><X size={18} aria-hidden /></button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => void yes()}>Turn on reminders</button>
        <button className="btn btn-sm btn-ghost" onClick={dismissReminderCard}>Not now</button>
      </div>
    </section>
  );
}

/** "16:30" → "4:30 PM" in the person's locale. */
export function formatTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(2000, 0, 1, h, m);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
